/**
 * Autotest surface contract for the logged-in **FocusedNav** (activation
 * population). Step 8 — AT-prep (plugs-and-sockets) + A/A launch. Modeled on the
 * SUTD bulk-download page socket AT (`AutotestSutdBulkDownload.ts`): `Surfaces` =
 * the slots a variant may override, `CONTROL` = the single source of every
 * default (today's shipped nav), `resolveSurfaces(arm, args)` returns the active
 * arm's overrides merged over CONTROL.
 *
 * This PR wires the sockets to the full variant test (`nav_activation_2026_q2_v1`,
 * control + 12) with `ARMS` EMPTY, so all 13 arms render byte-identically —
 * validating the bucketing + variant-delivery path on the socketed nav before
 * Step 9 fills `ARMS`.
 *
 * Divergence from the SUTD page AT (intentional): the nav is **SSR-prerendered**,
 * so a client-side bucket would flash. The arm is bucketed **server-side** in
 * `FocusedNavPresenter` and threaded in as the `navArm` prop, so `resolveSurfaces`
 * takes the arm as a parameter rather than calling the generated `abTest()`.
 */

import { TEST_CONFIG } from 'js/ab_tests/tests/nav_activation_2026_q2_v1';

// === Arms (control + 12) ===

export type Arm =
  | 'control'
  | 'v_cta_gold' // A1: gold-filled, bolder premium CTA
  | 'v_cta_duration' // B1: trial-duration framing ("30 days of Premium for $1")
  | 'v_cta_value_prop' // B2: value-prop copy instead of price/trial
  | 'v_cta_crown' // B3: positive crown/premium signifier instead of the lock
  | 'v_cta_pro_badge' // B4: "PRO" badge signifier instead of the lock
  | 'v_mentions_count' // C1: numeric count instead of the binary dot
  | 'v_mentions_cited_label' // C2: "See who cited you" label
  | 'v_mentions_cta' // C3: render the mentions item as a direct premium CTA
  | 'v_social_proof' // C4: live social-proof element near the CTA
  | 'v_account_upgrade_button' // D2: account-menu upgrade anchor as a styled button
  | 'v_account_avatar_upsell' // D3: upsell affordance on the account avatar
  | 'v_mobile_premium_tab'; // E1: mobile upsell as a bottom tab instead of top icon

/** Resolved arm. `undefined` = holdout / not bucketed → CONTROL. */
export type ResolvedArm = Arm | undefined;

// === Render-time inputs (so arms can quantify / anchor) ===

export type SurfaceArgs = {
  isPremium: boolean;
  isMobileNav: boolean;
  pageName: string;
  /** premium_upsells.trialPricingCopy — the trial CTA copy, or null. */
  trialPricingCopy: string | null;
  /** Unseen mentions count (already fetched client-side). */
  mentionsCount: number;
  /** Real formatted trial price from premium_upsells (e.g. "$1"); null when no
   * trial is available — the B1 duration variant then falls back to control. */
  trialPriceLabel: string | null;
  /** Real humanized trial period from premium_upsells (e.g. "1 month"); null when
   * no trial — the B1 duration variant then falls back to control. */
  trialPeriodLabel: string | null;
  /** NEW [Step 9 data] "N reading now / joined this week" for social proof (C4). */
  liveReaderCount: number | null;
};

// === Surfaces ===

export type CtaEmphasis = 'default' | 'gold';
export type CtaIcon = 'lock' | 'crown' | 'pro_badge';
export type MentionsBadge = 'dot' | 'count';
export type MobilePremiumPlacement = 'top_icon' | 'bottom_tab';

export type Surfaces = {
  /** A1. Visual treatment of the prominent premium CTA. */
  ctaEmphasis: CtaEmphasis;
  /** B1/B2. Premium CTA label. `null` = the component's offer-derived default
   * (trial/price copy), which can't be a static constant. */
  ctaLabel: string | null;
  /** B3/B4. The premium signifier icon on the CTA. */
  ctaIcon: CtaIcon;
  /** C2. Mentions nav-item label. */
  mentionsLabel: string;
  /** C1. Mentions unread indicator — binary dot vs numeric count. */
  mentionsBadge: MentionsBadge;
  /** C3. Render the mentions item as a direct premium CTA instead of icon→page. */
  mentionsAsCta: boolean;
  /** C4. Optional live social-proof line near the CTA. `null` = none (CONTROL). */
  socialProofSlot: string | null;
  /** D2. Account-menu upgrade anchor — plain link vs styled button. */
  accountUpgradeStyle: 'link' | 'button';
  /** D3. Upsell affordance (badge + mini-CTA) on the account avatar, non-premium. */
  accountAvatarUpsell: boolean;
  /** E1. Mobile premium placement — top-right icon vs bottom tab-bar tab. */
  mobilePremiumPlacement: MobilePremiumPlacement;
};

/** CONTROL is the single source of every default — today's shipped nav. */
export const CONTROL: Surfaces = {
  ctaEmphasis: 'default',
  ctaLabel: null,
  ctaIcon: 'lock',
  mentionsLabel: 'Mentions',
  mentionsBadge: 'dot',
  mentionsAsCta: false,
  socialProofSlot: null,
  accountUpgradeStyle: 'link',
  accountAvatarUpsell: false,
  mobilePremiumPlacement: 'top_icon',
};

// === ARMS ===
// EMPTY in the AT-prep / A/A PR — every arm → CONTROL → byte-identical rendering.
// Step 9 fills this with the 12 variant overrides, keyed by the arm names above.
type ArmSpec = (args: SurfaceArgs) => Partial<Surfaces>;

const ARMS: Partial<Record<Arm, ArmSpec>> = {
  // C1: show the numeric unseen-mentions count instead of the binary red dot.
  v_mentions_count: () => ({ mentionsBadge: 'count' }),
  // C2: value-framed mentions label instead of the neutral "Mentions".
  v_mentions_cited_label: () => ({ mentionsLabel: 'See who cited you' }),
  // B2: value-prop CTA copy (no price/trial). Benefit hook instead of the
  // offer-derived default. ctaLabel is threaded view → PremiumUpsellButton →
  // PremiumButton/PremiumText, where a non-null label overrides ctaText.
  v_cta_value_prop: () => ({ ctaLabel: 'Unlock 50M+ papers' }),
  // B1: reframe the trial around its DURATION rather than the bare price (most
  // control copy is already "Try Premium for $1", so a price restate ~= control).
  // Uses the REAL humanized period + price (e.g. "1 month of Premium for $1"); the
  // url/trigger stay tied to the offer. If either real value is missing (no trial
  // available), returns no override so the offer-derived control copy is used —
  // never invents a duration/price.
  v_cta_duration: ({ trialPeriodLabel, trialPriceLabel }) =>
    trialPeriodLabel && trialPriceLabel
      ? { ctaLabel: `${trialPeriodLabel} of Premium for ${trialPriceLabel}` }
      : {},
  // B3: replace the lock signifier (reads as "locked out") with a positive,
  // aspirational premium icon (Material `workspace_premium` crown/rosette).
  v_cta_crown: () => ({ ctaIcon: 'crown' }),
  // B4: replace the lock signifier with a "PRO" badge on the CTA.
  v_cta_pro_badge: () => ({ ctaIcon: 'pro_badge' }),
  // A1: gold-filled, bolder premium CTA vs today's blue button. Desktop-only —
  // the mobile CTA is a transparent text button that can't take a fill, and the
  // trial-CTA CTR metric is desktop-only anyway (see PremiumUpsellButton).
  v_cta_gold: () => ({ ctaEmphasis: 'gold' }),
  // D2: render the account-menu 'Upgrade to Premium' anchor as a styled button
  // (DS ButtonLink) instead of the plain styled link. Same href/trigger/copy.
  v_account_upgrade_button: () => ({ accountUpgradeStyle: 'button' }),
  // D3: surface a small "Upgrade" affordance on the account avatar button (the
  // most-clicked nav element) for non-premium users. The view/components gate on
  // premium, so this only renders for non-premium.
  v_account_avatar_upsell: () => ({ accountAvatarUpsell: true }),
  // C4: a live social-proof line near the premium CTA. CONTROL is null (renders
  // nothing), so only this arm shows the line; every other arm is unchanged.
  v_social_proof: () => ({
    socialProofSlot: 'Join 200+ researchers who upgraded this week',
  }),
  // C3: render the mentions item as a direct premium CTA ("See who cited you →")
  // for non-premium users with unseen mentions (gated in FocusedNavView).
  v_mentions_cta: () => ({ mentionsAsCta: true }),
  // E1: on mobile, move the premium upsell out of the top-right icon slot and
  // into a dedicated tab in the fixed BottomMenu (non-premium mobile only).
  v_mobile_premium_tab: () => ({ mobilePremiumPlacement: 'bottom_tab' }),
};

/**
 * Returns the arm's surface overrides merged over CONTROL. `arm` is the
 * server-bucketed value threaded from FocusedNavPresenter (or 'control'/undefined
 * for users not in the test). With ARMS empty this is always CONTROL.
 */
export function resolveSurfaces(
  arm: ResolvedArm,
  args: SurfaceArgs,
): Surfaces & { arm: ResolvedArm } {
  const spec = arm !== undefined ? ARMS[arm] : undefined;
  const overrides = spec ? spec(args) : {};
  return { arm, ...CONTROL, ...overrides };
}

/** Tag an impression with the resolved arm for per-arm attribution. */
export function impressionExtra(arm: ResolvedArm): Record<string, unknown> {
  const isActive: boolean = TEST_CONFIG.active as boolean;
  return {
    autotest_bucket: arm ?? 'control',
    ...(isActive && { autotest: 'nav_activation_2026_q2_v1' }),
  };
}
